import apiFetch from '@wordpress/api-fetch';
import { createReduxStore, register } from '@wordpress/data';

const DEFAULT_STATE = {
	appliedVouchers: {},
	appliedGiftCards: {},
	availablePromotions: {
		all: [],
		valid: [],
	},
	appliedPromotions: {},
	isLoading: false
};

const actions = {
	addPromotions( promotions ) {
		return {
			type: 'ADD_PROMOTIONS',
			promotions: promotions
		}
	},
	doAddPromotion(promotion) {
		return {
			type: 'ADD_PROMOTION',
			promotion: promotion
		}
	},
	doRemovePromotion(promotionCode) {
		return {
			type: 'REMOVE_PROMOTION',
			promotionCode: promotionCode
		};
	},


	addVouchers( vouchers ) {
		return {
			type: 'ADD_VOUCHERS',
			vouchers: vouchers
		}
	},

	addAvailablePromotions( promotions ) {
		return {
			type: 'ADD_AVAILABLE_PROMOTIONS',
			promotions
		}
	},

	addGiftCards( vouchers ) {
		return {
			type: 'ADD_GIFT_CARDS',
			vouchers: vouchers
		}
	},

	doAddVoucher(voucher) {
		return {
			type: 'ADD_VOUCHER',
			voucher: voucher
		}
	},

	doAddGiftCard(voucher) {
		return {
			type: 'ADD_GIFT_CARD',
			voucher: voucher
		}
	},

	doRemoveVoucher(voucherCode) {
		return {
			type: 'REMOVE_VOUCHER',
			voucherCode: voucherCode
		};
	},

	doRemoveGiftCard(voucherCode) {
		return {
			type: 'REMOVE_GIFT_CARD',
			voucherCode: voucherCode
		};
	},

	addPromotion(promotionCode) {
		return async ({dispatch}) => {
			try {
				const response = await apiFetch({
					path: "/voucherify/v1/promotions/apply",
					method: "POST",
					data: {code: promotionCode},
					cache: 'no-store',
				});

				dispatch.doAddPromotion({
					[response.code]: {
						amount: response.amount,
						name: response.name
					}
				});

				return response;
			} catch (error) {
				return Promise.reject(error);
			}
		};
	},
	removePromotion( promotionCode ) {
		return async ({dispatch}) => {
			try {
				const response = await apiFetch({
					path: '/voucherify/v1/promotions/remove/' + encodeURIComponent(promotionCode),
					method: 'DELETE',
					cache: 'no-store',
				});

				dispatch.doRemovePromotion(promotionCode);

				return response;
			} catch (error) {
				return Promise.reject(error);
			}
		};
	},

	addGiftCard(voucherCode, amount) {
		return async ({dispatch}) => {
			try {
				const response = await apiFetch({
					path: "/voucherify/v1/gift-cards/apply",
					method: "POST",
					data: {code: voucherCode, amount: amount},
					cache: 'no-store',
				});

				dispatch.doAddGiftCard({[response.code]: response.amount});

				return response;
			} catch (error) {
				return Promise.reject(error);
			}
		};
	},

	maybeAddVoucher(voucherCode) {
		return async ({dispatch}) => {
			try {
				const response = await apiFetch({
					path: "/voucherify/v1/discount-vouchers/apply",
					method: "POST",
					data: {code: voucherCode},
					cache: 'no-store',
				});

				if (response.type !== 'gift') {
					dispatch.doAddVoucher({
						[response.code]: response.amount
					});
				}

				return response;
			} catch (error) {
				return Promise.reject(error);
			}
		};
	},

	removeVoucher( voucherCode ) {
		return async ({dispatch}) => {
			try {
				const response = await apiFetch({
					path: '/voucherify/v1/discount-vouchers/remove/' + encodeURIComponent(voucherCode),
					method: 'DELETE',
					cache: 'no-store',
				});

				dispatch.doRemoveVoucher(voucherCode);

				return response;
			} catch (error) {
				return Promise.reject(error);
			}
		};
	},

	removeGiftCard( voucherCode ) {
		return async ({dispatch}) => {
			try {
				const response = await apiFetch({
					path: '/voucherify/v1/gift-cards/remove/' + encodeURIComponent(voucherCode),
					method: 'DELETE',
					cache: 'no-store',
				});

				dispatch.doRemoveGiftCard(voucherCode);

				return response;
			} catch (error) {
				return Promise.reject(error);
			}
		};
	},

	fetchFromAPI( path ) {
		return {
			type: 'FETCH_FROM_API',
			path,
		};
	},
};

const store = createReduxStore( 'voucherify', {
	reducer( state = DEFAULT_STATE, action ) {
		switch (action.type) {
			case 'ADD_AVAILABLE_PROMOTIONS':
				return {
					...state,
					availablePromotions: {
						all: action.promotions.all,
						valid: action.promotions.valid,
					}
				};
			case 'ADD_VOUCHERS':
				return {
					...state,
					appliedVouchers: {
						...state.appliedVouchers,
						...action.vouchers
					}
				};
			case 'ADD_VOUCHER':
				return {
					...state,
					appliedVouchers: {
						...state.appliedVouchers,
						...action.voucher
					}
				};
			case 'ADD_GIFT_CARDS':
				return {
					...state,
					appliedGiftCards: {
						...state.appliedGiftCards,
						...action.vouchers
					}
				};
			case 'ADD_GIFT_CARD':
				return {
					...state,
					appliedGiftCards: {
						...state.appliedGiftCards,
						...action.voucher
					}
				};
			case 'REMOVE_VOUCHER':
				return {
					...state,
					appliedVouchers: Object.keys(state.appliedVouchers)
						.filter(key => key !== action.voucherCode) // Replace 'voucherToRemove' with the key you want to remove
						.reduce((obj, key) => {
							obj[key] = state.appliedVouchers[key];
							return obj;
						}, {})
				};
			case 'REMOVE_GIFT_CARD':
				return {
					...state,
					appliedGiftCards: Object.keys(state.appliedGiftCards)
						.filter(key => key !== action.voucherCode) // Replace 'voucherToRemove' with the key you want to remove
						.reduce((obj, key) => {
							obj[key] = state.appliedGiftCards[key];
							return obj;
						}, {})
				};
			case 'ADD_PROMOTIONS':
				return {
					...state,
					appliedPromotions: {
						...state.appliedPromotions,
						...action.promotions
					}
				};
			case 'ADD_PROMOTION':
				return {
					...state,
					appliedPromotions: {
						...state.appliedPromotions,
						...action.promotion
					}
				};
			case 'REMOVE_PROMOTION':
				return {
					...state,
					appliedPromotions: Object.keys(state.appliedPromotions)
						.filter(key => key !== action.promotionCode)
						.reduce((obj, key) => {
							obj[key] = state.appliedPromotions[key];
							return obj;
						}, {})
				};
		}

		return state;
	},

	actions,

	selectors: {
		getAppliedPromotions( state ) {
			const { appliedPromotions } = state;
			return appliedPromotions;
		},
		getAvailablePromotions( state ) {
			const { availablePromotions } = state;
			return availablePromotions;
		},
		getAppliedVouchers( state ) {
			const { appliedVouchers } = state;
			return appliedVouchers;
		},
		getAppliedGiftCards( state ) {
			const { appliedGiftCards } = state;
			return appliedGiftCards;
		},
		isApplyingVoucher( state ) {
			const { isApplyingVoucher } = state;
			return isApplyingVoucher;
		},
		isRemovingVoucher( state ) {
			const { isRemovingVoucher } = state;
			return isRemovingVoucher;
		}
	},

	controls: {
		FETCH_FROM_API( action ) {
			return apiFetch( { path: action.path } );
		},
	},

	resolvers: {
		*getAppliedVouchers(){
			const path = '/voucherify/v1/discount-vouchers';
			const appliedVouchersResponse = yield actions.fetchFromAPI( path );
			return actions.addVouchers(appliedVouchersResponse.applied_vouchers);
		},
		*getAppliedGiftCards(){
			const path = '/voucherify/v1/gift-cards';
			const appliedGiftCardsResponse = yield actions.fetchFromAPI( path );
			return actions.addGiftCards(appliedGiftCardsResponse.applied_gift_cards);
		},
		*getAppliedPromotions(){
			const path = '/voucherify/v1/promotions';
			const appliedPromotionsResponse = yield actions.fetchFromAPI( path );
			return actions.addPromotions(appliedPromotionsResponse.applied_promotions);
		},
		*getAvailablePromotions() {
			const path = '/voucherify/v1/promotions/available';
			const promotions = yield actions.fetchFromAPI( path );
			return actions.addAvailablePromotions(promotions);
		}
	},
} );

register( store );
